import { ArrowDownTrayIcon, ArrowsPointingOutIcon, TrashIcon } from '@heroicons/react/24/outline'
import { AspectRatio, Box, Chip, Grid, IconButton, Stack, Typography } from '@mui/joy'
import { JoyStyles } from '@types'
import React from 'react'
import { FloatingLabelInput } from '@components/FloatingLabelInput'
import { globalStyles } from '@styles/styles'

const DraggableIcon = ({ hidden }: { hidden?: boolean }) => (
  <IconButton
    variant="soft"
    disabled
    sx={{ visibility: hidden ? 'hidden' : 'visible' }}
  >
    <ArrowsPointingOutIcon
      width={'1em'}
      style={{ transform: 'rotate(45deg)' }}
    />
  </IconButton>
)

const IsCoverIcon = () => (
  <Chip sx={{ paddingX: 2, backgroundColor: 'var(--joy-palette-background-surface)' }}>
    <Typography level="body-md-bold">Portada</Typography>
  </Chip>
)

const DeleteIcon = ({ onClick, width, hidden }: { onClick?: () => void; width?: string; hidden?: boolean }) => (
  <IconButton
    variant="soft"
    onClick={onClick}
    sx={{ visibility: hidden ? 'hidden' : 'visible' }}
  >
    <TrashIcon width={width ?? '1em'} />
  </IconButton>
)

const DownloadIcon = ({ onClick, width, hidden }: { onClick?: () => void; width?: string; hidden?: boolean }) => (
  <IconButton
    variant="soft"
    onClick={onClick}
    sx={{ visibility: hidden ? 'hidden' : 'visible' }}
  >
    <ArrowDownTrayIcon width={width ?? '1em'} />
  </IconButton>
)

interface Image {
  file?: string | File
  text_alt?: string
}

interface ImageGridProps {
  images?: Image[]
  onReorder?: (from: number, to: number) => void
  onDelete?: (index: number) => void
  onAltChange?: (index: number, alt: string) => void
  placeholder?: string
  label?: string
  hideAltTextInput?: boolean
  customColumnNumber?: number
  showCover?: boolean
  showDownload?: boolean
}

export const ImageGrid = ({
  images,
  onReorder,
  onDelete,
  onAltChange,
  label,
  placeholder,
  hideAltTextInput,
  customColumnNumber,
  showCover,
  showDownload,
}: ImageGridProps) => {
  return (
    <Grid
      container
      display={'flex'}
    >
      {images?.map((image, index) => (
        <Grid
          xs={6}
          md={customColumnNumber ?? 3}
          key={index}
        >
          <ImageGridItem
            placeholder={placeholder}
            noReorder={images.length === 1}
            label={label}
            image={image}
            index={index}
            onReorder={onReorder}
            onDelete={onDelete}
            onAltChange={onAltChange}
            hideAltTextInput={hideAltTextInput}
            isCover={showCover && !index}
            showDownload={showDownload}
          />
        </Grid>
      ))}
    </Grid>
  )
}

interface ImageGridItemProps {
  image: Image
  index: number
  onReorder?: (from: number, to: number) => void
  onDelete?: (index: number) => void
  onAltChange?: (index: number, alt: string) => void
  noReorder?: boolean
  label?: string
  placeholder?: string
  hideAltTextInput?: boolean
  isCover?: boolean
  showDownload?: boolean
}

const ImageGridItem = ({
  image,
  index,
  onReorder,
  onDelete,
  onAltChange,
  noReorder,
  label,
  placeholder,
  hideAltTextInput,
  isCover,
  showDownload,
}: ImageGridItemProps) => {
  const styles: JoyStyles = {
    aspectRatioStack: {
      justifyContent: 'space-between',
      padding: 1,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    },
  }

  return (
    <Box
      sx={{
        padding: 1,
        ...(isCover && {
          backgroundColor: 'var(--joy-palette-neutral-softBg)',
          borderRadius: globalStyles.borderRadius,
        }),
      }}
    >
      <Stack gap={1}>
        <ImageGridItemImage
          image={image.file}
          index={index}
          onReorder={onReorder}
        >
          <Stack
            direction="row"
            sx={styles.aspectRatioStack}
          >
            <DraggableIcon hidden={noReorder} />
            {isCover && <IsCoverIcon />}
            {Boolean(onDelete) && <DeleteIcon onClick={() => onDelete?.(index)} />}
          </Stack>
          {showDownload && (
            <Stack
              direction="row"
              sx={{ ...styles.aspectRatioStack, top: undefined, bottom: 0 }}
            >
              <DraggableIcon hidden />
              <DownloadIcon
                onClick={() => {
                  if (!image.file) return
                  const url = typeof image.file === 'string' ? image.file : URL.createObjectURL(image.file)
                  const a = document.createElement('a')
                  a.href = url
                  a.download =
                    typeof image.file === 'string'
                      ? image.file.substring(image.file.lastIndexOf('/') + 1)
                      : image.file?.name
                  a.click()
                }}
              />
            </Stack>
          )}
        </ImageGridItemImage>
        {!hideAltTextInput && (
          <FloatingLabelInput
            placeholder={placeholder}
            label={label ?? 'Descripción'}
            name={`altText-${index}`}
            value={image?.text_alt ?? ''}
            onChange={(e) => onAltChange?.(index, e.target.value)}
          />
        )}
      </Stack>
    </Box>
  )
}

interface ImageGridItemImageProps {
  image?: string | File
  children?: React.ReactNode
  index: number
  onReorder?: (from: number, to: number) => void
}

const ImageGridItemImage = ({ image, children, index, onReorder }: ImageGridItemImageProps) => {
  return (
    <AspectRatio
      sx={{ cursor: 'grabbing', position: 'relative', borderRadius: globalStyles.borderRadius }}
      objectFit="contain"
      draggable
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        e.persist()
        onReorder?.(Number(e.dataTransfer.getData('index')), index)
      }}
      onDragStart={(e) => e.dataTransfer.setData('index', index.toString())}
    >
      <img
        draggable={false}
        src={typeof image === 'string' ? image : image instanceof File ? URL.createObjectURL(image) : ''}
      />
      {children}
    </AspectRatio>
  )
}

interface FileGridProps {
  files?: { file?: string | File }[]
  onDelete?: (index: number) => void
}

export const FileGrid = ({ files, onDelete }: FileGridProps) => {
  return (
    <Stack
      spacing={1}
      sx={{ mt: 1 }}
    >
      {files?.map(({ file }, index) => (
        <Box
          key={index}
          sx={{ border: globalStyles.border, borderRadius: globalStyles.borderRadius, p: 1, width: 'fit-content' }}
        >
          <Typography
            level="body-md-light"
            endDecorator={
              <IconButton>
                <TrashIcon
                  onClick={() => onDelete?.(index)}
                  style={{ width: '1.5rem' }}
                />
              </IconButton>
            }
          >
            {typeof file === 'string' ? file.substring(file.lastIndexOf('/') + 1) : file?.name}
          </Typography>
        </Box>
      ))}
    </Stack>
  )
}

import { DocumentIcon, PhotoIcon, TableCellsIcon } from '@heroicons/react/24/outline'
import { Checkbox } from '@mui/joy'

interface FilePro {
  file?: string | File
  public_name?: string
  is_active?: boolean
  created_at?: string
}

interface FileGridProProps {
  files?: FilePro[]
  onReorder?: (from: number, to: number) => void
  onDelete?: (index: number) => void
  onAltChange?: (index: number, alt: string) => void
  onCheckChange?: (index: number, checked: boolean) => void
  placeholder?: string
  label?: string
  showDownload?: boolean
}

export const FileGridPro = ({
  files,
  label,
  onReorder,
  onDelete,
  onAltChange,
  placeholder,
  onCheckChange,
  showDownload,
}: FileGridProProps) => {
  return (
    <Grid
      container
      spacing={2}
    >
      {files?.map((file, index) => (
        <Grid
          xs={6}
          md={3}
          key={index}
        >
          <FileGridProItem
            placeholder={placeholder}
            noReorder={files.length === 1}
            label={label}
            file={file}
            index={index}
            onReorder={onReorder}
            onDelete={onDelete}
            onAltChange={onAltChange}
            onCheckChange={onCheckChange}
            showDownload={showDownload}
          />
        </Grid>
      ))}
    </Grid>
  )
}

interface FileGridProItemProps {
  file: FilePro
  index: number
  onReorder?: (from: number, to: number) => void
  onDelete?: (index: number) => void
  onAltChange?: (index: number, alt: string) => void
  noReorder?: boolean
  label?: string
  placeholder?: string
  onCheckChange?: (index: number, checked: boolean) => void
  showDownload?: boolean
}

const FileGridProItem = ({
  file,
  index,
  onReorder,
  onDelete,
  onAltChange,
  noReorder,
  label,
  placeholder,
  onCheckChange,
  showDownload,
}: FileGridProItemProps) => {
  const styles: JoyStyles = {
    aspectRatioStack: {
      justifyContent: 'space-between',
      padding: 1,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    },
  }

  return (
    <Stack gap={1}>
      <FileGridProItemImage
        index={index}
        onReorder={onReorder}
        file={file.file}
      >
        <Stack
          direction="row"
          sx={styles.aspectRatioStack}
        >
          <DraggableIcon hidden={noReorder || !Boolean(onReorder)} />
          <DeleteIcon
            hidden={!Boolean(onDelete)}
            onClick={() => onDelete?.(index)}
          />
        </Stack>
        {showDownload && (
          <Stack
            direction="row"
            sx={{ ...styles.aspectRatioStack, top: undefined, bottom: 0 }}
          >
            <DraggableIcon hidden />
            <DownloadIcon
              onClick={() => {
                if (!file.file) return
                const url = typeof file.file === 'string' ? file.file : URL.createObjectURL(file.file)
                const a = document.createElement('a')
                a.href = url
                a.download =
                  typeof file.file === 'string' ? file.file.substring(file.file.lastIndexOf('/') + 1) : file.file?.name
                a.click()
              }}
            />
          </Stack>
        )}
      </FileGridProItemImage>
      <Stack>
        <Typography
          level="body-md-light"
          sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {typeof file.file === 'string' ? file.file.substring(file.file.lastIndexOf('/') + 1) : file.file?.name}
        </Typography>
        {file.created_at && (
          <Typography level="body-sm-light">{'Creado en: ' + new Date(file.created_at).toLocaleString()}</Typography>
        )}
      </Stack>
      {Boolean(onAltChange) && (
        <FloatingLabelInput
          placeholder={placeholder}
          label={label ?? 'Nombre público del archivo'}
          name={`altText-${index}`}
          value={file?.public_name ?? ''}
          onChange={(e) => onAltChange?.(index, e.target.value)}
        />
      )}
      {Boolean(onCheckChange) && (
        <Checkbox
          checked={Boolean(file.is_active)}
          onChange={(e) => onCheckChange?.(index, e.target.checked)}
          label="Publicar en sitio web"
        />
      )}
    </Stack>
  )
}

interface FileGridProItemImageProps {
  children?: React.ReactNode
  index: number
  onReorder?: (from: number, to: number) => void
  file?: FilePro['file']
}

const iconWidth = '40px'

const iconsByFileType = {
  jpg: (
    <PhotoIcon
      onDrop={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      width={iconWidth}
    />
  ),
  jpeg: (
    <PhotoIcon
      onDrop={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      width={iconWidth}
    />
  ),
  png: (
    <PhotoIcon
      onDrop={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      width={iconWidth}
    />
  ),
  webp: (
    <PhotoIcon
      onDrop={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      width={iconWidth}
    />
  ),
  pdf: (
    <DocumentIcon
      onDrop={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      width={iconWidth}
    />
  ),
  doc: (
    <DocumentIcon
      onDrop={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      width={iconWidth}
    />
  ),
  docx: (
    <DocumentIcon
      onDrop={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      width={iconWidth}
    />
  ),
  xls: (
    <TableCellsIcon
      onDrop={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      width={iconWidth}
    />
  ),
  xlsx: (
    <TableCellsIcon
      onDrop={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      width={iconWidth}
    />
  ),
}

const FileGridProItemImage = ({ file, children, index, onReorder }: FileGridProItemImageProps) => {
  const fileName = typeof file === 'string' ? file.substring(file.lastIndexOf('/') + 1) : file?.name
  const fileExtension = fileName?.split('.').pop()
  return (
    <AspectRatio
      sx={{ cursor: 'grabbing', position: 'relative', borderRadius: globalStyles.borderRadius }}
      objectFit="contain"
      draggable
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        e.persist()
        onReorder?.(Number(e.dataTransfer.getData('index')), index)
      }}
      onDragStart={(e) => e.dataTransfer.setData('index', index.toString())}
    >
      <Stack sx={{ textAlign: 'center' }}>
        {iconsByFileType[fileExtension?.toLowerCase() as keyof typeof iconsByFileType] ?? (
          <DocumentIcon
            onDrop={(e) => e.preventDefault()}
            onDragOver={(e) => e.preventDefault()}
            width={'40px'}
          />
        )}
      </Stack>
      {children}
    </AspectRatio>
  )
}

import { ArrowPathRoundedSquareIcon } from '@heroicons/react/24/outline'
import { Tour360, Video } from '@types'
import { embedPrefixes, multimedia_360_tours_options, multimedia_video_options } from '@constants'

interface VideoGridProps {
  videos?: Video[] | Tour360[]
  onReorder?: (from: number, to: number) => void
  onDelete?: (index: number) => void
}

export const VideoGrid = ({ videos, onReorder, onDelete }: VideoGridProps) => {
  return (
    <Grid
      container
      spacing={2}
    >
      {videos?.map((video, index) => (
        <Grid
          xs={6}
          md={4}
          key={index}
        >
          <VideoGridItem
            noReorder={videos.length === 1}
            video={video}
            index={index}
            onReorder={onReorder}
            onDelete={onDelete}
          />
        </Grid>
      ))}
    </Grid>
  )
}

interface VideoGridItemProps {
  video: Video | Tour360
  index: number
  onReorder?: (from: number, to: number) => void
  onDelete?: (index: number) => void
  noReorder?: boolean
}

const VideoGridItem = ({ index, onReorder, onDelete, noReorder, video }: VideoGridItemProps) => {
  const styles: JoyStyles = {
    aspectRatioStack: {
      justifyContent: 'space-between',
      padding: 1,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    },
  }

  return (
    <Stack gap={1}>
      <VideoGridItemImage
        urlId={video.urlId}
        platform={video.platform}
        index={index}
        onReorder={onReorder}
      >
        <Stack
          direction="row"
          sx={styles.aspectRatioStack}
        >
          <DraggableIcon hidden={noReorder} />
          <DeleteIcon onClick={() => onDelete?.(index)} />
        </Stack>
      </VideoGridItemImage>
      <Stack>
        <Typography level="body-md-bold">{video.title}</Typography>
        <a
          href={video.url}
          target="_blank"
          rel="noreferrer"
        >
          <Typography level="body-sm-light">{video.url}</Typography>
        </a>
      </Stack>
    </Stack>
  )
}

type PlatformVideo = (typeof multimedia_video_options)[number]
type Platform360 = (typeof multimedia_360_tours_options)[number]

interface VideoGridItemImageProps {
  urlId?: string
  platform?: PlatformVideo | Platform360
  children?: React.ReactNode
  index: number
  onReorder?: (from: number, to: number) => void
}

const VideoGridItemImage = ({ urlId, platform, children, index, onReorder }: VideoGridItemImageProps) => {
  return (
    <AspectRatio
      sx={{ cursor: 'grabbing', position: 'relative', borderRadius: globalStyles.borderRadius }}
      objectFit="contain"
      draggable
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        e.persist()
        onReorder?.(Number(e.dataTransfer.getData('index')), index)
      }}
      onDragStart={(e) => e.dataTransfer.setData('index', index.toString())}
    >
      {([...multimedia_video_options] as string[]).includes(platform ?? '') ? (
        <iframe
          onDrop={(e) => e.preventDefault()}
          onDragOver={(e) => e.preventDefault()}
          draggable={false}
          src={`${embedPrefixes[platform as keyof typeof embedPrefixes]}${urlId}`}
        />
      ) : (
        <Stack sx={{ textAlign: 'center' }}>
          360º
          <ArrowPathRoundedSquareIcon
            onDrop={(e) => e.preventDefault()}
            onDragOver={(e) => e.preventDefault()}
            width={'40px'}
          />
        </Stack>
      )}
      {children}
    </AspectRatio>
  )
}
